import { Divider, Typography, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { FieldErrors } from 'react-hook-form';
import * as Yup from 'yup';

import Button from '@/components/Button';
import useResponsive from '@/hooks/useResponsive';
import { ProductResponse } from '@/providers/HttpProvider/Fulfill/Inventory/types';

import { sendToClientSchema } from '../../inventorySchema';
import { ProductsBox } from '../../SendToWarehouse/dependencies/AddUnits';
import WarehouseSelector from '../../WarehouseSelector';

type SendToClientFormData = Yup.InferType<typeof sendToClientSchema>;

export interface StepProps {
  step: number;
  setCurrentStep: (step: number) => void;
  products?: ProductResponse['products'];
  onChange: ({ index, sku, quantity }: { index: number; sku: string; quantity: string }) => void;
  errors: FieldErrors<SendToClientFormData>;
  isDisabled: boolean;
  items?: { sku: string; quantity: number }[];
  editStep: boolean;
  warehouse?: string;
  onWarehouseChange?: (value: string) => void;
  warehouseOptions?: { value: string; label: string }[];
  isLoadingWarehouses?: boolean;
}

const StepUnits = ({
  step,
  setCurrentStep,
  products,
  onChange,
  errors,
  isDisabled,
  items,
  editStep,
  warehouse = '',
  onWarehouseChange,
  warehouseOptions = [],
  isLoadingWarehouses = false,
}: StepProps) => {
  const theme = useTheme();
  const isSmallDrawer = useResponsive('between', 'lg', 'xl');
  const isPhone = useResponsive('down', 'sm');

  const totalUnits = items?.reduce((acc, cur) => acc + (cur.quantity || 0), 0) || 0;

  const totalVolume =
    products?.reduce((acc, product, index) => {
      const quantity = items?.[index]?.quantity ?? 0;
      if (product.dimensions) {
        // Assumption: length, width, and height are in centimeters (cm).
        // The calculated volume is in cubic centimeters (cm³).
        const { length, width, height } = product.dimensions;
        const volumeInCm3 = length * width * height * quantity;
        return acc + volumeInCm3;
      }
      return acc;
    }, 0) || 0;

  const totalVolumeInCm3 = totalVolume.toFixed(0);

  const getFieldError = (index: number): string | undefined => {
    const itemsErrors = errors?.items;
    if (Array.isArray(itemsErrors) && itemsErrors[index]) {
      const itemError = itemsErrors[index];
      if (itemError && typeof itemError === 'object' && 'quantity' in itemError) {
        const quantityError = itemError.quantity;
        if (quantityError && typeof quantityError === 'object' && 'message' in quantityError) {
          return quantityError.message as string;
        }
      }
    }
    return undefined;
  };
  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          margin: '1rem 0',
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.4rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>1</span>
        </Typography>
        <Divider
          orientation={isSmallDrawer || isPhone ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            margin: isSmallDrawer || isPhone ? '0' : `0 ${theme.spacing(2)}`,
          }}
        />
        <Box>
          <Typography variant="subtitle2">¿Cuántas unidades deseas enviar?</Typography>
        </Box>
      </Box>

      <Grid container size={{ xxs: 12 }}>
        <Grid
          container
          size={{ xxs: 12 }}
          sx={{
            display: 'flex',
            justifyContent: 'space-around',
            alignItems: 'center',
            padding: theme.spacing(2),
          }}
        >
          <Typography variant="body1" color={theme.palette.grey[500]}>
            Producto
          </Typography>
          <Typography variant="body1" color={theme.palette.grey[500]}>
            Unidades
          </Typography>
        </Grid>
        {
          <Grid
            container
            spacing={2}
            size={{ xxs: 12 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '0.5rem',
              border: '1px solid',
              borderColor: theme.palette.grey[300],
              borderRadius: '1rem 1rem 0 0',
              padding: theme.spacing(2),
            }}
          >
            {products?.map((product, index) => (
              <ProductsBox
                key={index}
                product={product}
                index={index}
                onChange={onChange}
                value={items?.[index]?.quantity}
                name={`items[${index}]?.quantity`}
                errors={getFieldError(index)}
              />
            ))}
          </Grid>
        }
        <Grid
          container
          size={{ xxs: 12 }}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            border: '1px solid',
            borderColor: theme.palette.grey[300],
            borderTop: '1px solid',
            borderTopColor: theme.palette.grey[300],
            borderRadius: '0 0 1rem 1rem',
            padding: theme.spacing(2),
            gap: '0.5rem',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              gap: '0.5rem',
            }}
          >
            <Typography variant="body2" fontWeight="bold">
              {`Producto${products && products.length > 1 ? 's' : ''}: ${products?.length}`}
            </Typography>{' '}
            <Typography variant="body2" fontWeight="bold">
              Volumen estimado: {totalVolumeInCm3} cm³
            </Typography>
          </Box>
          <Typography variant="body2" fontWeight="bold" color={theme.palette.primary.main}>
            Total de unidades: {totalUnits}
          </Typography>
        </Grid>
      </Grid>

      <Grid size={{ xxs: 12 }}>
        <WarehouseSelector
          label="Almacén de origen"
          value={warehouse}
          onChange={(value) => onWarehouseChange?.(value)}
          placeholder="Asignación automática (más cercano)"
          title={
            <>
              ¿<span style={{ color: theme.palette.primary.main }}>Desde qué almacén</span> deseas
              enviar?
            </>
          }
          infoText="Si no selecciona un almacén, se asignará automáticamente el más cercano. De lo contrario, indique cuál desea utilizar."
          options={warehouseOptions}
          isLoading={isLoadingWarehouses}
        />
      </Grid>

      <Grid container sx={{ display: 'flex', justifyContent: 'end' }} mt={3}>
        <Button
          disabled={isDisabled}
          onClick={() => (editStep ? setCurrentStep(4) : setCurrentStep(step + 1))}
        >
          Continuar
        </Button>
      </Grid>
    </Box>
  );
};

export default StepUnits;
